/**
 * tokenManager – single source of truth for acquiring the ID token sent to
 * the backend, and for handling an expired session.
 *
 * Why this exists:
 *   - acquireTokenSilent() only checks the *access token* expiry. It happily
 *     returns a cached idToken that has already expired, so the backend 401s
 *     and the UI silently fails to load. We check idTokenClaims.exp ourselves
 *     and force a refresh when it is (nearly) expired.
 *   - Once the session is known to be dead, further API calls are blocked
 *     instead of being fired and failing one by one.
 *   - A `window` event (SESSION_EXPIRED_EVENT) lets the UI tell the user why
 *     data isn't loading and offer a "Sign in again" button.
 */
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  clearStaleMsalTemporaryCache,
  loginRequest,
  msalInstance,
  silentRedirectUri,
} from './msalConfig';

export const SESSION_EXPIRED_EVENT = 'authSessionExpired';

// Refresh the idToken if it expires within this window.
const EXPIRY_SKEW_SECONDS = 5 * 60;

// Share one in-flight acquisition across concurrent requests so MSAL's hidden
// iframe isn't spawned several times at once (causes timed_out errors).
let _tokenPromise = null;
let _sessionExpired = false;
let _redirectPending = false;
let _cacheHealAttempted = false;

export class SessionExpiredError extends Error {
  constructor(reason) {
    super('Your session has expired. Please sign in again.');
    this.name = 'SessionExpiredError';
    this.isSessionExpired = true;
    this.reason = reason;
  }
}

export function isSessionExpired() {
  return _sessionExpired;
}

function isIdTokenExpiring(result) {
  const exp = result?.idTokenClaims?.exp;
  if (!exp) return !result?.idToken;
  return exp - EXPIRY_SKEW_SECONDS <= Math.floor(Date.now() / 1000);
}

function silentRequest(account, forceRefresh = false) {
  return msalInstance.acquireTokenSilent({
    account,
    scopes: loginRequest.scopes,
    redirectUri: silentRedirectUri,
    forceRefresh,
  });
}

function needsInteraction(error) {
  return (
    error instanceof InteractionRequiredAuthError ||
    error?.errorCode === 'interaction_required' ||
    error?.errorCode === 'login_required' ||
    error?.errorCode === 'consent_required' ||
    error?.errorCode === 'no_tokens_found' ||
    error?.errorCode === 'no_account_error' ||
    error?.errorCode === 'timed_out' ||
    error?.errorCode === 'monitor_window_timeout'
  );
}

/** Start the interactive sign-in redirect (once). */
export function signInAgain() {
  if (_redirectPending) return;
  _redirectPending = true;
  msalInstance.loginRedirect(loginRequest).catch((redirectError) => {
    console.error('[auth] loginRedirect failed:', redirectError);
    // Allow the user to retry from the banner.
    _redirectPending = false;
  });
}

/**
 * Mark the session as expired, notify the UI and start the sign-in redirect.
 * Subsequent API calls are rejected without hitting the network.
 */
export function handleSessionExpired(reason) {
  if (!_sessionExpired) {
    _sessionExpired = true;
    console.warn(`[auth] Session expired (${reason})`);
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } }));
  }
  signInAgain();
}

async function acquire(account) {
  let result;
  try {
    result = await silentRequest(account);
  } catch (error) {
    const transient =
      error?.errorCode === 'interaction_in_progress' ||
      error?.errorCode === 'timed_out' ||
      error?.errorCode === 'monitor_window_timeout';
    if (!transient || _cacheHealAttempted) throw error;

    _cacheHealAttempted = true;
    const removedKeys = clearStaleMsalTemporaryCache();
    console.warn(`[auth] Cleared stale MSAL temporary keys: ${removedKeys}. Retrying silent token.`);
    result = await silentRequest(account);
  }

  // Cached idToken may be expired even though the access token is not.
  if (isIdTokenExpiring(result)) {
    result = await silentRequest(account, true);
    if (isIdTokenExpiring(result)) {
      throw new SessionExpiredError('id_token_expired');
    }
  }
  return result.idToken;
}

/**
 * Returns a valid ID token, or throws SessionExpiredError after triggering the
 * expired-session flow. Throws the raw MSAL error for non-auth failures.
 */
export async function getIdToken() {
  if (_sessionExpired) throw new SessionExpiredError('session_expired');

  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) throw new Error('No signed-in account found');

  if (!_tokenPromise) {
    _tokenPromise = acquire(account).finally(() => { _tokenPromise = null; });
  }

  try {
    return await _tokenPromise;
  } catch (error) {
    if (error?.isSessionExpired || needsInteraction(error)) {
      const reason = error?.reason || error?.errorCode || 'token_acquisition_failed';
      handleSessionExpired(reason);
      throw error?.isSessionExpired ? error : new SessionExpiredError(reason);
    }
    console.warn('[auth] Token acquisition failed:', error?.errorCode, error?.message);
    throw error;
  }
}
